import {
    AlignmentType,
    BorderStyle,
    Document,
    HeadingLevel,
    ImageRun,
    Packer,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    TextRun,
    WidthType,
    convertMillimetersToTwip
} from 'docx';

const HEADER_BLUE = '0B4787';
const MUTED_TEXT = '6C757D';
const TABLE_HEADER_FILL = 'F5F5F5';
const INFO_LABEL_FILL = 'EAEAEA';
const TABLE_BORDER_COLOR = '808080';

const LIGHT_BORDER = {
    style: BorderStyle.SINGLE,
    size: 4,
    color: TABLE_BORDER_COLOR
};

const TABLE_BORDERS = {
    top: LIGHT_BORDER,
    bottom: LIGHT_BORDER,
    left: LIGHT_BORDER,
    right: LIGHT_BORDER,
    insideHorizontal: LIGHT_BORDER,
    insideVertical: LIGHT_BORDER
};

const NO_BORDERS = {
    top: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    bottom: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
    insideVertical: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
};

const waitForMapRender = (mapEl, timeout = 10000) => {
    return new Promise((resolve) => {
        if (!mapEl) return resolve();

        const spinner = mapEl.querySelector('.spinner');
        if (!spinner) {
            return requestAnimationFrame(() => resolve());
        }

        const observer = new MutationObserver(() => {
            if (!mapEl.querySelector('.spinner')) {
                observer.disconnect();
                requestAnimationFrame(() => resolve());
            }
        });

        observer.observe(mapEl, { childList: true, subtree: true });

        setTimeout(() => {
            observer.disconnect();
            resolve();
        }, timeout);
    });
};

const normalizeColor = (color, fallback = 'FFFFFF') => (color || fallback).replace('#', '');

const normalizeText = (value, fallback = '-') => {
    if (value === 0) return '0';
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    return text || fallback;
};

const dataUrlToUint8Array = (dataUrl) => {
    const [, base64] = dataUrl.split(',');
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

const EXPORT_MAP_WIDTH = 620;
const EXPORT_MAP_HEIGHT = 465;

const captureMapImage = (mapEl) => {
    if (!mapEl) return null;

    const canvases = mapEl.querySelectorAll('.ol-viewport canvas');
    if (!canvases || canvases.length === 0) return null;

    try {
        const first = canvases[0];
        const source = document.createElement('canvas');
        source.width = first.width;
        source.height = first.height;
        const sourceCtx = source.getContext('2d');
        canvases.forEach(canvas => sourceCtx.drawImage(canvas, 0, 0));

        const target = document.createElement('canvas');
        target.width = EXPORT_MAP_WIDTH;
        target.height = EXPORT_MAP_HEIGHT;
        const targetCtx = target.getContext('2d');
        targetCtx.fillStyle = '#ffffff';
        targetCtx.fillRect(0, 0, EXPORT_MAP_WIDTH, EXPORT_MAP_HEIGHT);

        const targetRatio = EXPORT_MAP_WIDTH / EXPORT_MAP_HEIGHT;
        const sourceRatio = source.width / source.height;
        let sx = 0, sy = 0, sw = source.width, sh = source.height;
        if (sourceRatio > targetRatio) {
            sw = source.height * targetRatio;
            sx = (source.width - sw) / 2;
        } else if (sourceRatio < targetRatio) {
            sh = source.width / targetRatio;
            sy = (source.height - sh) / 2;
        }

        targetCtx.drawImage(source, sx, sy, sw, sh, 0, 0, EXPORT_MAP_WIDTH, EXPORT_MAP_HEIGHT);

        return {
            data: dataUrlToUint8Array(target.toDataURL('image/png')),
            width: EXPORT_MAP_WIDTH,
            height: EXPORT_MAP_HEIGHT
        };
    } catch (e) {
        return null;
    }
};

const downloadBlob = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('lv-LV');
};

const formatFileDate = (value) => {
    if (!value) return 'eksports';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'eksports';
    return date.toISOString().slice(0, 10);
};

const createParagraphsFromText = (value, options = {}) => {
    const {
        fallback = '-',
        bold = false,
        italics = false,
        color,
        alignment,
        size,
        spacingAfter = 120
    } = options;

    const text = normalizeText(value, fallback);
    const lines = String(text).split('\n');

    return lines.map((line, index) => new Paragraph({
        alignment,
        spacing: { after: index === lines.length - 1 ? spacingAfter : 60 },
        children: [
            new TextRun({
                text: line || ' ',
                bold,
                italics,
                color,
                size
            })
        ]
    }));
};

const createSectionHeading = (title) => [
    new Paragraph({
        spacing: { before: 0, after: 0, line: 200 },
        keepNext: true,
        children: [new TextRun(' ')]
    }),
    new Paragraph({
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 0, after: 0, line: 400 },
        keepNext: true,
        keepLines: true,
        shading: { fill: HEADER_BLUE },
        indent: { left: 200, right: 200 },
        children: [new TextRun({
            text: title,
            bold: true,
            color: 'FFFFFF',
            size: 28
        })]
    }),
    new Paragraph({
        spacing: { before: 0, after: 0, line: 120 },
        keepNext: true,
        children: [new TextRun(' ')]
    })
];

const createEmptyParagraph = () => new Paragraph({
    spacing: { after: 120 },
    children: [new TextRun(' ')]
});

const createDataTable = ({ headers = [], rows = [], widths = [] }) => {
    const tableRows = [];

    if (headers.length > 0) {
        tableRows.push(new TableRow({
            tableHeader: true,
            children: headers.map((header, index) => new TableCell({
                width: widths[index] ? { size: widths[index], type: WidthType.PERCENTAGE } : undefined,
                shading: { fill: TABLE_HEADER_FILL },
                margins: { top: 90, bottom: 90, left: 110, right: 110 },
                children: createParagraphsFromText(header, {
                    bold: true,
                    spacingAfter: 0
                })
            }))
        }));
    }

    rows.forEach((row) => {
        tableRows.push(new TableRow({
            children: row.map((cell, index) => {
                const cellConfig = typeof cell === 'object' && cell !== null && !Array.isArray(cell)
                    ? cell
                    : { text: cell };

                return new TableCell({
                    width: widths[index] ? { size: widths[index], type: WidthType.PERCENTAGE } : undefined,
                    shading: cellConfig.fill ? { fill: normalizeColor(cellConfig.fill) } : undefined,
                    margins: { top: 90, bottom: 90, left: 110, right: 110 },
                    children: createParagraphsFromText(cellConfig.text, {
                        fallback: cellConfig.fallback !== undefined ? cellConfig.fallback : '-',
                        bold: !!cellConfig.bold,
                        italics: !!cellConfig.italics,
                        color: cellConfig.color,
                        alignment: cellConfig.alignment,
                        spacingAfter: 0
                    })
                });
            })
        }));
    });

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: TABLE_BORDERS,
        rows: tableRows
    });
};

const createInfoTable = (pamatinfoRows = [], zemesLietojumsRows = []) => {
    const rows = pamatinfoRows.map((row) => new TableRow({
        children: [
            new TableCell({
                width: { size: 35, type: WidthType.PERCENTAGE },
                shading: { fill: INFO_LABEL_FILL },
                margins: { top: 90, bottom: 90, left: 110, right: 110 },
                children: createParagraphsFromText(row.label, {
                    bold: true,
                    spacingAfter: 0
                })
            }),
            new TableCell({
                width: { size: 65, type: WidthType.PERCENTAGE },
                margins: { top: 90, bottom: 90, left: 110, right: 110 },
                children: createParagraphsFromText(row.value, {
                    spacingAfter: 0
                })
            })
        ]
    }));

    if (zemesLietojumsRows.length > 0) {
        rows.push(new TableRow({
            children: [
                new TableCell({
                    width: { size: 35, type: WidthType.PERCENTAGE },
                    shading: { fill: INFO_LABEL_FILL },
                    margins: { top: 90, bottom: 90, left: 110, right: 110 },
                    children: createParagraphsFromText('Zemes lietošanas veids', {
                        bold: true,
                        spacingAfter: 0
                    })
                }),
                new TableCell({
                    width: { size: 65, type: WidthType.PERCENTAGE },
                    margins: { top: 90, bottom: 90, left: 110, right: 110 },
                    children: [
                        createDataTable({
                            headers: ['Zemes lietošanas veids', 'Izplatība, %'],
                            rows: zemesLietojumsRows.map((item) => [item.veids, item.izplatiba]),
                            widths: [70, 30]
                        })
                    ]
                })
            ]
        }));
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: TABLE_BORDERS,
        rows
    });
};

const createRiskColumnTable = (items = []) => {
    if (items.length === 0) {
        return new Paragraph({
            spacing: { after: 0 },
            children: [new TextRun(' ')]
        });
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: NO_BORDERS,
        rows: items.map((item) => new TableRow({
            children: [
                new TableCell({
                    width: { size: 65, type: WidthType.PERCENTAGE },
                    borders: NO_BORDERS,
                    margins: { top: 60, bottom: 60, left: 0, right: 80 },
                    children: createParagraphsFromText(item.label, {
                        bold: true,
                        spacingAfter: 0
                    })
                }),
                new TableCell({
                    width: { size: 35, type: WidthType.PERCENTAGE },
                    borders: NO_BORDERS,
                    shading: { fill: normalizeColor(item.color, 'F8F9FA') },
                    margins: { top: 60, bottom: 60, left: 80, right: 80 },
                    children: createParagraphsFromText(item.riskLabel, {
                        bold: true,
                        alignment: AlignmentType.CENTER,
                        spacingAfter: 0
                    })
                })
            ]
        }))
    });
};

const createBulletParagraphs = (items = []) => {
    if (items.length === 0) {
        return createParagraphsFromText('Nav datu', {
            color: MUTED_TEXT
        });
    }

    return items.map(item => new Paragraph({
        bullet: { level: 0 },
        spacing: { after: 80 },
        children: [new TextRun(normalizeText(item))]
    }));
};

export const exportKopsavilkumsFromDom = async (_containerEl, mapEl, exportData = {}) => {
    if (!exportData) return;

    await waitForMapRender(mapEl);
    const mapImage = captureMapImage(mapEl);

    const children = [
        new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 220 },
            children: [
                new TextRun({
                    text: 'DZERAMĀ ŪDENS IEGUVES VIETU SATECES BASEINA RISKA NOVĒRTĒJUMS',
                    bold: true,
                    color: HEADER_BLUE,
                    size: 30
                })
            ]
        }),
        ...createSectionHeading('Sateces baseina pamatinformācija'),
        createInfoTable(exportData.pamatinfoRows, exportData.zemesLietojumsRows),
        ...createSectionHeading('Sateces baseina aptveres zona')
    ];

    if (mapImage) {
        children.push(new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 140 },
            children: [
                new ImageRun({
                    data: mapImage.data,
                    transformation: {
                        width: mapImage.width,
                        height: mapImage.height
                    }
                })
            ]
        }));
    } else {
        children.push(...createParagraphsFromText('Karte nav pieejama', {
            italics: true,
            color: MUTED_TEXT
        }));
    }

    if ((exportData.aizsargjoslasRows || []).length > 0) {
        children.push(
            ...createSectionHeading('Aizsargjoslas ap ūdens ieguves vietām'),
            createDataTable({
                headers: ['Ūdens ieguves vietas DB Nr.', 'Stingra režīma aizsargjosla', 'Bakterioloģiskā aizsargjosla', 'Ķīmiskā aizsargjosla', 'Žogs ap urbumu', 'Piezīmes'],
                rows: exportData.aizsargjoslasRows,
                widths: [14, 18, 16, 14, 12, 26]
            })
        );
    }

    children.push(...createSectionHeading('Riska novērtējuma kopsavilkums'));

    const riskColumns = exportData.riskaKategorijuKolonnas || [[], []];
    const hasRiskItems = riskColumns.some(column => column.length > 0);

    const categoriesGrid = hasRiskItems ? new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: NO_BORDERS,
        rows: [
            new TableRow({
                cantSplit: true,
                children: [
                    new TableCell({
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        borders: NO_BORDERS,
                        margins: { top: 0, bottom: 0, left: 0, right: 100 },
                        children: [createRiskColumnTable(riskColumns[0])]
                    }),
                    new TableCell({
                        width: { size: 50, type: WidthType.PERCENTAGE },
                        borders: NO_BORDERS,
                        margins: { top: 0, bottom: 0, left: 100, right: 0 },
                        children: [createRiskColumnTable(riskColumns[1])]
                    })
                ]
            })
        ]
    }) : null;

    const noRiskItemsParagraphs = !hasRiskItems ? createParagraphsFromText('Nav riska kategoriju datu', {
        color: MUTED_TEXT
    }) : [];

    const galaTable = new Table({
        alignment: AlignmentType.CENTER,
        width: { size: 60, type: WidthType.PERCENTAGE },
        borders: NO_BORDERS,
        rows: [
            new TableRow({
                cantSplit: true,
                children: [
                    new TableCell({
                        width: { size: 60, type: WidthType.PERCENTAGE },
                        borders: NO_BORDERS,
                        margins: { top: 120, bottom: 120, left: 0, right: 100 },
                        children: createParagraphsFromText('Gala novērtējums:', {
                            bold: true,
                            alignment: AlignmentType.RIGHT,
                            spacingAfter: 0
                        })
                    }),
                    new TableCell({
                        width: { size: 40, type: WidthType.PERCENTAGE },
                        borders: NO_BORDERS,
                        shading: { fill: normalizeColor(exportData.galaRiskLevel?.color, 'F8F9FA') },
                        margins: { top: 120, bottom: 120, left: 100, right: 100 },
                        children: createParagraphsFromText(exportData.galaRiskLevel?.label, {
                            fallback: '-',
                            bold: true,
                            alignment: AlignmentType.CENTER,
                            spacingAfter: 0
                        })
                    })
                ]
            })
        ]
    });

    children.push(new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: NO_BORDERS,
        rows: [new TableRow({
            cantSplit: true,
            children: [new TableCell({
                borders: NO_BORDERS,
                margins: { top: 0, bottom: 0, left: 0, right: 0 },
                children: [
                    ...(categoriesGrid ? [categoriesGrid] : noRiskItemsParagraphs),
                    galaTable
                ]
            })]
        })]
    }));

    children.push(
        ...createSectionHeading('Riska novērtējuma slēdziens'),
        ...(exportData.sledziens
            ? createParagraphsFromText(exportData.sledziens)
            : createParagraphsFromText('Nav ievadīts slēdziens', {
                color: MUTED_TEXT
            }))
    );

    if ((exportData.obligatieParametri || []).length > 0 || (exportData.rekomendetieParametri || []).length > 0) {
        children.push(
            ...createSectionHeading('Monitoringā iekļaujamo parametru saraksts'),
            createDataTable({
                headers: ['Obligāti nosakāmie parametri*', 'Papildus rekomendējamie parametri*'],
                rows: [[
                    { text: (exportData.obligatieParametri || []).join(', '), fallback: '' },
                    { text: (exportData.rekomendetieParametri || []).join(', '), fallback: '' }
                ]],
                widths: [50, 50]
            }),
            ...createParagraphsFromText(
                '* neapstrādātā pazemes ūdens monitoringā nosakāmie rādītāji, atbilstoši veiktajam riska novērtējumam',
                {
                    color: MUTED_TEXT,
                    size: 20
                }
            )
        );
    }

    children.push(
        ...createSectionHeading('Riska pārvaldības pasākumi'),
        ...createParagraphsFromText('Ietekmi mazinošie pasākumi', {
            bold: true,
            spacingAfter: 80
        }),
        ...((exportData.ietekmesPasakumi || []).length > 0
            ? createBulletParagraphs(exportData.ietekmesPasakumi)
            : createParagraphsFromText('Nav ietekmes mazinošo pasākumu', {
                color: MUTED_TEXT
            })),
        ...createParagraphsFromText('Preventīvie pasākumi', {
            bold: true,
            spacingAfter: 80
        }),
        ...((exportData.preventiviePasakumi || []).length > 0
            ? createBulletParagraphs(exportData.preventiviePasakumi)
            : createParagraphsFromText('Nav preventīvo pasākumu', {
                color: MUTED_TEXT
            }))
    );

    if ((exportData.piezimes || []).length > 0) {
        children.push(
            ...createSectionHeading('Piezīmes'),
            ...exportData.piezimes.flatMap(text => createParagraphsFromText(text))
        );
    }

    children.push(
        createEmptyParagraph(),
        new Paragraph({
            spacing: { before: 120 },
            children: [
                new TextRun({
                    text: `Riska novērtējums sagatavošanas datums: ${formatDate(exportData.datums)}`,
                    color: MUTED_TEXT,
                    size: 20
                })
            ]
        })
    );

    const doc = new Document({
        creator: 'Izraktenis',
        title: 'Sateces baseina riska novērtējuma kopsavilkums',
        sections: [{
            properties: {
                page: {
                    margin: {
                        top: convertMillimetersToTwip(15),
                        right: convertMillimetersToTwip(15),
                        bottom: convertMillimetersToTwip(15),
                        left: convertMillimetersToTwip(15)
                    }
                }
            },
            children
        }]
    });

    const blob = await Packer.toBlob(doc);
    downloadBlob(blob, `sateces-baseina-riska-novertejums-${formatFileDate(exportData.datums)}.docx`);
};

export default exportKopsavilkumsFromDom;
